import * as RadixScrollArea from '@radix-ui/react-scroll-area';
import React, {
  useCallback,
  type ReactNode,
  type RefObject,
  type UIEvent,
  type UIEventHandler,
} from 'react';
import { useScrollShadows } from '@/hooks/useScrollShadows';

interface Props {
  className?: string;
  viewportClassName?: string;
  scrollbarClassName?: string;
  children: ReactNode;
  hasTopShadow?: boolean;
  hasBottomShadow?: boolean;
  viewportRef?: RefObject<HTMLDivElement | null>;
  onScroll?: UIEventHandler<HTMLDivElement>;
}

export const AutoHideScrollArea: React.FC<Props> = ({
  className,
  viewportClassName = '',
  scrollbarClassName = '',
  children,
  hasTopShadow = false,
  hasBottomShadow = false,
  viewportRef,
  onScroll,
}) => {
  const { scrollRef, topProgress, bottomProgress, handleScroll } =
    useScrollShadows();

  const hasScrollShadows = hasTopShadow || hasBottomShadow;

  const setViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (hasScrollShadows) {
        scrollRef(node);
      }

      if (viewportRef) {
        viewportRef.current = node;
      }
    },
    [hasScrollShadows, scrollRef, viewportRef],
  );

  const handleViewportScroll = (event: UIEvent<HTMLDivElement>) => {
    onScroll?.(event);

    if (hasScrollShadows) {
      handleScroll(event);
    }
  };

  return (
    <RadixScrollArea.Root
      type="scroll"
      className={`relative ${className ?? ''}`}
    >
      {hasTopShadow && (
        <div
          style={{ opacity: topProgress }}
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-constant-black/6 to-transparent transition-opacity duration-300 ease-out"
        />
      )}
      {hasBottomShadow && (
        <div
          style={{ opacity: bottomProgress }}
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 bg-gradient-to-t from-constant-black/6 to-transparent transition-opacity duration-300 ease-out"
        />
      )}
      <RadixScrollArea.Viewport
        ref={setViewportRef}
        onScroll={handleViewportScroll}
        className={`size-full overscroll-contain [&>div]:block! ${viewportClassName}`}
      >
        {children}
      </RadixScrollArea.Viewport>
      <RadixScrollArea.Scrollbar
        orientation="vertical"
        forceMount
        className={`z-10 flex w-[6px] touch-none pr-[0.5px] opacity-70 transition-opacity duration-300 ease-out select-none data-[state=hidden]:opacity-0 ${scrollbarClassName}`}
      >
        <RadixScrollArea.Thumb className="relative flex-1 rounded-full bg-[var(--scrollbar-thumb)]" />
      </RadixScrollArea.Scrollbar>
    </RadixScrollArea.Root>
  );
};
